import type { Client } from './client.js';
import { ErreurApi } from './client.js';
import { EXEMPLES, routeParId, type Ecran } from './contrat.js';

/** États d’une lecture, tels que l’écran les affiche : chargement, vide (avec explication), erreur (avec action), ok. */
export type EtatRequete<T = unknown> =
  { etat: 'chargement' } | { etat: 'ok'; donnees: T } | { etat: 'vide' } | { etat: 'erreur'; message: string };

export const ETAT_CHARGEMENT: EtatRequete = { etat: 'chargement' };

const estVide = (donnees: unknown): boolean => {
  if (donnees === null || donnees === undefined) return true;
  if (Array.isArray(donnees)) return donnees.length === 0;
  if (typeof donnees === 'object' && 'items' in donnees) return Array.isArray(donnees.items) && donnees.items.length === 0;
  return false;
};

export async function chargerRequete<T = unknown>(
  client: Client,
  routeId: string,
  entree?: { parametres?: Record<string, string>; requete?: Record<string, string> },
): Promise<EtatRequete<T>> {
  try {
    const donnees = await client.appeler<T>(routeId, entree);
    return estVide(donnees) ? { etat: 'vide' } : { etat: 'ok', donnees };
  } catch (e) {
    if (e instanceof ErreurApi) return { etat: 'erreur', message: e.messageUtilisateur };
    return { etat: 'erreur', message: 'Une erreur est survenue. Réessayez dans un instant ; si elle persiste, contactez le Cabinet MAAT.' };
  }
}

/** Charge les lectures déclarées d’un écran, avec les paramètres d’exemple du contrat (mode démonstration sur mocks). */
export async function chargerEcran(client: Client, ecran: Ecran): Promise<Record<string, EtatRequete>> {
  const resultats = await Promise.all(
    ecran.lectures.map(async (id) => {
      routeParId(id);
      const exemple = EXEMPLES[id];
      const entree = {
        ...(exemple?.parametres ? { parametres: exemple.parametres } : {}),
        ...(exemple?.requete ? { requete: exemple.requete } : {}),
      };
      return [id, await chargerRequete(client, id, entree)] as const;
    }),
  );
  return Object.fromEntries(resultats);
}
